import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Box, Typography, Chip } from '@mui/material';
import { friendlyNameConverter } from '../../../../services/localizationService';
import { filterByWindow } from '../statistics';
import { useTimeline } from '../TimelineContext';
import { useChart } from '../../../../components/chart';
import { buildCollapsedTimeline, realToCollapsed, collapsedToReal } from '../gapCollapse';

export default function MultiOverlayWidget({ recordings = [], widgetConfig }) {
  const { currentTime, seek, window: timeWindow } = useTimeline();
  const recId = widgetConfig?.recId || '';
  const sensorKey1 = widgetConfig?.sensorKey1 || '';
  const sensorKey2 = widgetConfig?.sensorKey2 || '';

  const rec = recordings.find((r) => r.id === recId);

  // Build collapsed timeline from all recordings
  const collapsedTimeline = useMemo(() => {
    const recs = recordings.map((r) => ({ id: r.id, startTime: r.startTime, endTime: r.endTime }));
    return buildCollapsedTimeline(recs);
  }, [recordings]);

  const hasCollapsedTimeline = collapsedTimeline.segments.length > 0;

  const chartData = useMemo(() => {
    if (!rec?.data || !sensorKey1) return null;
    let points1 = rec.data[sensorKey1] || [];
    let points2 = sensorKey2 ? (rec.data[sensorKey2] || []) : [];

    if (timeWindow) {
      points1 = filterByWindow(points1, timeWindow.start, timeWindow.end);
      points2 = sensorKey2 ? filterByWindow(points2, timeWindow.start, timeWindow.end) : [];
    }

    if (points1.length === 0) return null;

    if (hasCollapsedTimeline) {
      // Map to collapsed time, using [x, y] format for numeric xaxis
      const realTimes = points1.map((dp) => dp.time);

      const series1Data = points1.map((dp) => {
        const collapsed = realToCollapsed(dp.time, collapsedTimeline);
        return [collapsed ?? dp.time, dp.value];
      });
      const series = [{ type: 'line', name: sensorKey1, data: series1Data }];

      if (sensorKey2 && points2.length > 0) {
        const series2Data = points2.map((dp) => {
          const collapsed = realToCollapsed(dp.time, collapsedTimeline);
          return [collapsed ?? dp.time, dp.value];
        });
        series.push({ type: 'line', name: sensorKey2, data: series2Data });
      }

      return { series, realTimes };
    }

    const series = [
      { type: 'line', name: friendlyNameConverter(sensorKey1), data: points1.map((dp) => dp.value) },
    ];
    const labels = points1.map((dp) => dp.time);

    if (sensorKey2 && points2.length > 0) {
      series.push({ type: 'line', name: friendlyNameConverter(sensorKey2), data: points2.map((dp) => dp.value) });
    }

    return { series, labels };
  }, [rec, sensorKey1, sensorKey2, timeWindow, hasCollapsedTimeline, collapsedTimeline]);

  const name1 = friendlyNameConverter(sensorKey1 || '');
  const name2 = friendlyNameConverter(sensorKey2 || '');

  // Build gap indicator annotations
  const gapAnnotations = useMemo(() => {
    if (!hasCollapsedTimeline) return [];
    return collapsedTimeline.segments
      .filter((seg) => seg.type === 'gap')
      .map((seg) => ({
        x: seg.collapsedStart,
        x2: seg.collapsedEnd,
        fillColor: 'rgba(0,0,0,0.06)',
        opacity: 1,
        label: { text: '' },
      }));
  }, [hasCollapsedTimeline, collapsedTimeline]);

  // Map currentTime playhead to collapsed space
  const collapsedCurrentTime = hasCollapsedTimeline
    ? realToCollapsed(currentTime, collapsedTimeline)
    : currentTime;

  // Compute collapsed min/max for x-axis range
  const collapsedMin = hasCollapsedTimeline
    ? (timeWindow?.start != null ? realToCollapsed(timeWindow.start, collapsedTimeline) : 0)
    : timeWindow?.start;
  const collapsedMax = hasCollapsedTimeline
    ? (timeWindow?.end != null ? realToCollapsed(timeWindow.end, collapsedTimeline) : collapsedTimeline.totalCollapsedDuration)
    : timeWindow?.end;

  const chartOptions = useChart({
    fill: { type: chartData?.series.map(() => 'solid') || ['solid'] },
    ...(hasCollapsedTimeline ? {} : { labels: chartData?.labels || [] }),
    xaxis: hasCollapsedTimeline
      ? {
          type: 'numeric',
          min: collapsedMin ?? 0,
          max: collapsedMax ?? collapsedTimeline.totalCollapsedDuration,
          labels: {
            formatter: (val) => {
              const realTime = collapsedToReal(val, collapsedTimeline);
              if (realTime == null) return '';
              const d = new Date(realTime);
              return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
            },
          },
          tickAmount: 8,
        }
      : { type: 'datetime', min: timeWindow?.start, max: timeWindow?.end, labels: { datetimeUTC: false } },
    ...(sensorKey2 && {
      yaxis: [
        { title: { text: name1 } },
        { opposite: true, title: { text: name2 } },
      ],
    }),
    ...(!sensorKey2 && { yaxis: { title: { text: name1 } } }),
    annotations: {
      xaxis: [
        ...gapAnnotations,
        ...(collapsedCurrentTime != null ? [{
          x: collapsedCurrentTime,
          borderColor: '#775DD0',
          label: { borderColor: '#775DD0', style: { color: '#fff', background: '#775DD0' }, text: 'Now' },
        }] : []),
      ],
    },
    chart: {
      toolbar: { show: true, tools: { download: true, zoom: true, pan: true } },
      events: {
        markerClick: (_, __, opts) => {
          // Seek to real time, not collapsed time
          if (hasCollapsedTimeline && chartData?.realTimes?.[opts.dataPointIndex]) {
            seek(chartData.realTimes[opts.dataPointIndex]);
          } else if (chartData?.labels?.[opts.dataPointIndex]) {
            seek(chartData.labels[opts.dataPointIndex]);
          }
        },
      },
    },
    legend: { position: 'bottom' },
    tooltip: {
      custom: ({ series, dataPointIndex, w }) => {
        // Show real time in tooltip header
        let timeStr = '';
        if (hasCollapsedTimeline && chartData?.realTimes?.[dataPointIndex]) {
          const d = new Date(chartData.realTimes[dataPointIndex]);
          timeStr = `<div style="font-size:11px;color:#666;margin-bottom:4px">${d.toLocaleString()}</div>`;
        }
        const items = series.map((s, i) => {
          const val = s[dataPointIndex] ?? 'N/A';
          const color = w.globals.colors[i];
          const label = w.globals.seriesNames[i];
          return `<div style="display:flex;align-items:center"><span style="background:${color};border-radius:50%;width:10px;height:10px;margin-right:6px"></span>${label}: <b>${val}</b></div>`;
        }).join('');
        return `<div style="padding:8px">${timeStr}${items}</div>`;
      },
    },
  });

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {chartData ? (
        <Box sx={{ flex: 1, overflow: 'hidden', px: 1 }}>
          {timeWindow && <Chip label="Windowed" size="small" color="info" variant="outlined" sx={{ mb: 0.5, height: 18, fontSize: '0.6rem' }} />}
          <ReactApexChart type="line" series={chartData.series} options={chartOptions} height="90%" />
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">Select a recording and sensors</Typography>
        </Box>
      )}
    </Box>
  );
}
